/* Draws the 1080x1080 shareable player card from Player Passport data. */
import { fitFont, initials, loadImage, wrap } from './shareCard';

const SIZE = 1080;
const DARK = '#0a0a0a';
const TEAL = '#00d4aa';
const RED = '#e63946';
const LINE = '#2a2a2a';
const MUTED = '#92959f';
const AVATAR_TONES = ['#123b33', '#1d2f45', '#3a2130', '#2a2f1c'];

export const shortName = name => {
  const parts = (name || '').trim().split(/\s+/);
  if (parts.length < 2) return parts[0] || 'Player';
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
};

export const drawPlayerCard = async ({ athlete, metric, qrCanvas, logoSrc, photoSrc, sport = 'BASEBALL' }) => {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (document.fonts?.ready) { try { await document.fonts.ready; } catch { /* fonts are optional */ } }
  const [logo, photo] = await Promise.all([logoSrc ? loadImage(logoSrc) : null, photoSrc ? loadImage(photoSrc) : null]);
  const display = shortName(athlete.name);

  ctx.fillStyle = DARK;
  ctx.fillRect(0, 0, SIZE, SIZE);
  ctx.strokeStyle = LINE;
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, SIZE - 2, SIZE - 2);
  ctx.textBaseline = 'top';

  if (logo) {
    const height = 62;
    ctx.drawImage(logo, 72, 66, (logo.width / logo.height) * height, height);
  } else {
    ctx.fillStyle = RED;
    ctx.font = '700 44px "Barlow Condensed", sans-serif';
    ctx.fillText('SHC', 72, 70);
  }
  ctx.textAlign = 'right';
  ctx.fillStyle = TEAL;
  ctx.font = '500 22px "JetBrains Mono", monospace';
  ctx.fillText('THE PLAYER COLLECTION', SIZE - 72, 84);
  ctx.textAlign = 'left';

  const cx = 248;
  const cy = 330;
  const radius = 134;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  if (photo) {
    const scale = Math.max((radius * 2) / photo.width, (radius * 2) / photo.height);
    const w = photo.width * scale;
    const h = photo.height * scale;
    ctx.drawImage(photo, cx - w / 2, cy - radius, w, h);
  } else {
    ctx.fillStyle = AVATAR_TONES[initials(display).charCodeAt(0) % AVATAR_TONES.length];
    ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
    ctx.fillStyle = TEAL;
    ctx.font = '700 118px "Barlow Condensed", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials(display), cx, cy + 6);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
  }
  ctx.restore();
  ctx.strokeStyle = LINE;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(cx, cy, radius + 6, 0, Math.PI * 2);
  ctx.stroke();

  const textX = 438;
  const textWidth = SIZE - textX - 72;
  const nameSize = fitFont(ctx, display, 700, 76, 40, '"Barlow Condensed", sans-serif', textWidth);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(display, textX, 212);
  ctx.fillStyle = RED;
  ctx.fillRect(textX, 212 + nameSize + 14, Math.min(ctx.measureText(display).width, textWidth), 6);

  let y = 212 + nameSize + 44;
  ctx.fillStyle = TEAL;
  ctx.font = '600 30px "Barlow Condensed", sans-serif';
  wrap(ctx, (athlete.positions || [athlete.position]).filter(Boolean).join(' · '), textWidth).slice(0, 2).forEach(line => {
    ctx.fillText(line, textX, y);
    y += 38;
  });
  const facts = [`Age ${athlete.age}`, athlete.bats && `Bats ${athlete.bats}`, athlete.throws && `Throws ${athlete.throws}`].filter(Boolean);
  ctx.fillStyle = MUTED;
  ctx.font = '400 26px "Barlow Condensed", sans-serif';
  ctx.fillText(facts.join('  ·  '), textX, y + 4);

  if (metric) {
    ctx.fillStyle = TEAL;
    ctx.font = '500 20px "JetBrains Mono", monospace';
    ctx.fillText(metric.name.toUpperCase(), textX, y + 58);
    ctx.fillStyle = '#ffffff';
    ctx.font = '700 58px "Barlow Condensed", sans-serif';
    const value = `${metric.value}${metric.unit ? ' ' + metric.unit : ''}`;
    ctx.fillText(value, textX, y + 88);
  }

  ctx.strokeStyle = LINE;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(72, 570);
  ctx.lineTo(SIZE - 72, 570);
  ctx.stroke();

  const qrSize = 312;
  const qrX = 72;
  const qrY = 646;
  ctx.fillStyle = '#101010';
  ctx.fillRect(qrX - 14, qrY - 14, qrSize + 28, qrSize + 28);
  ctx.strokeStyle = LINE;
  ctx.strokeRect(qrX - 14, qrY - 14, qrSize + 28, qrSize + 28);
  if (qrCanvas) ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

  const ctaX = qrX + qrSize + 66;
  const ctaWidth = SIZE - ctaX - 72;
  ctx.fillStyle = '#ffffff';
  ctx.font = '700 40px "Barlow Condensed", sans-serif';
  let ctaY = qrY + 20;
  wrap(ctx, `Scan to coach ${display} — you’ll land connected`, ctaWidth).forEach(line => {
    ctx.fillText(line, ctaX, ctaY);
    ctaY += 50;
  });
  ctx.fillStyle = TEAL;
  ctx.font = '500 20px "JetBrains Mono", monospace';
  ctx.fillText('COACH INVITATION', ctaX, ctaY + 10);
  ctx.fillStyle = MUTED;
  ctx.font = '400 22px "JetBrains Mono", monospace';
  wrap(ctx, 'Sign up or sign in as a coach — no email needed', ctaWidth).forEach((line, index) => {
    ctx.fillText(line, ctaX, ctaY + 44 + index * 28);
  });

  ctx.fillStyle = MUTED;
  ctx.font = '500 24px "JetBrains Mono", monospace';
  ctx.fillText(`sportshubconnect.com  |  ${sport.toUpperCase()}`, 72, SIZE - 76);

  return canvas;
};
